import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    List,
    ListItem,
    ListItemText,
    TextField,
} from '@mui/material';
import useReportsData from "../hooks/useReportsData";

const PaymentStatusReport = forwardRef(({
    onGenerate,
    cutoffDate,
    setCutoffDate,
    loanFrom,
    setLoanFrom,
    loanTo,
    setLoanTo,
    sortOption,
    setSortOption,
    tabValue,
    setTabValue,
    selectedFunds,
    setSelectedFunds,
    selectedCounty,
    setSelectedCounty,
    selectedCity,
    setSelectedCity,
    selectedStatus,
    setSelectedStatus,
    selectedLoanOfficer,
    setSelectedLoanOfficer,
    selectedLoanType,
    setSelectedLoanType,
    selectedBorrower,
    setSelectedBorrower
}, ref) => {
    // Get common filter data from hook
    const {
        loannums,
        citys,
        loanofficers,
        borrowerTypeList,
        countys,
        loanTypes,
        loanStatusList,
        fundSources,
    } = useReportsData();

    // Handle list selection change
    const handleListSelectionChange = (value, list, setList) => {
        if (list.includes(value)) {
            setList(list.filter(item => item !== value));
        } else {
            setList([...list, value]);
        }
    };

    // Tab configuration
    const tabPanels = [
        {
            label: 'Funds',
            options: fundSources.map(fs => fs.code + ' ' + fs.description),
            selected: selectedFunds,
            setSelected: setSelectedFunds
        },
        {
            label: 'County',
            options: countys.map(c => c.county),
            selected: selectedCounty,
            setSelected: setSelectedCounty
        },
        {
            label: 'City',
            options: citys.map(c => c.city),
            selected: selectedCity,
            setSelected: setSelectedCity
        },
        {
            label: 'Status',
            options: loanStatusList.map(ls => ls.statusCode),
            selected: selectedStatus,
            setSelected: setSelectedStatus
        },
        {
            label: 'Loan Officer',
            options: loanofficers.map(lo => lo.loanOfficer),
            selected: selectedLoanOfficer,
            setSelected: setSelectedLoanOfficer
        },
        {
            label: 'Loan Type',
            options: loanTypes.map(lt => lt.code + ' ' + lt.description),
            selected: selectedLoanType,
            setSelected: setSelectedLoanType
        },
        {
            label: 'Borrower Type',
            options: borrowerTypeList.map(b => b.code + ' ' + b.description),
            selected: selectedBorrower,
            setSelected: setSelectedBorrower
        },
    ];

    const sortOptions = ['# Loan Number', 'A  Alphabetical', '$ Loan Amount', 'Loan Officer', 'Payment Due Date'];

    const handleGenerate = () => {
        // Helper function to join list items or return null
        const joinOrNull = (list) => {
            return list && list.length > 0 ? list : null;
        };

        const payload = {
            reportName: 'Payment Status Report',
            cutoffDate,
            loanFrom: loanFrom ? parseInt(loannums.find(x => x.id === loanFrom)?.loanNumber ?? "0") : 0,
            loanTo: loanTo ? parseInt(loannums.find(x => x.id === loanTo)?.loanNumber ?? "0") : 0,
            sortOption,
            selectedFunds: joinOrNull(selectedFunds),
            selectedCounty: joinOrNull(selectedCounty),
            selectedCity: joinOrNull(selectedCity),
            selectedStatus: joinOrNull(selectedStatus),
            selectedLoanOfficer: joinOrNull(selectedLoanOfficer),
            selectedLoanType: joinOrNull(selectedLoanType),
            selectedBorrower: joinOrNull(selectedBorrower),
        };

        console.log('Payment Status Report Payload:', payload);

        if (onGenerate) {
            onGenerate(payload);
        }
    };

    // Expose handleGenerate to parent component
    useImperativeHandle(ref, () => ({
        generateReport: handleGenerate
    }));

    return (
        <Box>
            {/* Cutoff Date */}
            <Box display="flex" gap={2} mb={2}>
                <TextField
                    label="Cutoff Date"
                    type="date"
                    value={cutoffDate}
                    onChange={e => setCutoffDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                />
            </Box>

            {/* Loan Range */}
            <Box display="flex" gap={2} mb={2}>
                <FormControl fullWidth>
                    <InputLabel>Loan From</InputLabel>
                    <Select
                        value={loanFrom}
                        onChange={e => setLoanFrom(e.target.value)}
                        label="Loan From"
                    >
                        <MenuItem value="">
                            <em>Select Loan</em>
                        </MenuItem>
                        {loannums.map((loannum) => (
                            <MenuItem key={loannum.id} value={loannum.id}>
                                {loannum.loanNumber} {loannum.loanName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel>Loan To</InputLabel>
                    <Select
                        value={loanTo}
                        onChange={e => setLoanTo(e.target.value)}
                        label="Loan To"
                    >
                        <MenuItem value="">
                            <em>Select Loan</em>
                        </MenuItem>
                        {loannums.map((loannum) => (
                            <MenuItem key={loannum.id} value={loannum.id}>
                                {loannum.loanNumber} {loannum.loanName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Sort Options */}
            <Box display="flex" gap={2} mb={2}>
                <FormControl fullWidth>
                    <InputLabel>Sort Options</InputLabel>
                    <Select
                        value={sortOption}
                        onChange={e => setSortOption(e.target.value)}
                        label="Sort Options"
                    >
                        {sortOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Filter Tabs */}
            <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                {tabPanels.map((tab, idx) => (
                    <Tab key={idx} label={tab.label} />
                ))}
            </Tabs>

            <Box
                mt={2}
                sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    border: '1px solid #ccc',
                    borderRadius: 1,
                    p: 1
                }}
            >
                <List>
                    {tabPanels[tabValue].options.map((option, index) => (
                        <ListItem
                            key={index}
                            button
                            onClick={() =>
                                handleListSelectionChange(
                                    option,
                                    tabPanels[tabValue].selected,
                                    tabPanels[tabValue].setSelected
                                )
                            }
                            selected={tabPanels[tabValue].selected.includes(option)}
                        >
                            <ListItemText
                                primary={option}
                                secondary={
                                    tabPanels[tabValue].selected.includes(option)
                                        ? 'Selected'
                                        : ''
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </Box>
        </Box>
    );
});

export default PaymentStatusReport;
